import { useEffect, useMemo, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import IcCloseBold from 'src/assets/images/icCloseBold.svg';
import SubscriptionPurchaseButton from 'src/components/AzarOnly/Subscription/SubscriptionPurchaseButton';
import { MobileModalType, ModalFooter, ModalLayoutProps } from 'src/components/ModalLayout';
import { useSubscriptionEventData, useSubscriptionPageQuery } from 'src/hooks/useSubscription';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { INVENTORY_ITEM_CATEGORY_ID_TYPES } from 'src/types/itemInventory';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
export { SUBSCRIPTION_GUIDE_LAND_FROM, INITIAL_FEATURE } from './constants';
export type { SubscriptionGuideLandFrom, InitialFeature } from './constants';
import { buildBenefitTableData } from './buildBenefitTableData';
import type { SubscriptionGuideLandFrom } from './constants';
import { BlurImage, BlurTopWrapper, Container, LeftPanel, ListWrapper, ModalContent, ModalLayout, MobileCloseButton, RightPanel, TabsWrapper, Title, TitleSection, LabelContainer, FirstTimeDiscountLabel } from './styles';
import SubscriptionFeatureList from './SubscriptionFeatureList';
import SubscriptionTabs from './SubscriptionTabs';
import { useSubscriptionGuideButton } from './useSubscriptionGuideButton';
import WebOnlyDiscountLabel from './WebOnlyDiscountLabel';
interface Props extends ModalLayoutProps {
  defaultPageId?: string;
  initialFeature?: string;
  landFrom: SubscriptionGuideLandFrom;
}
const SubscriptionGuideModal = ({
  defaultPageId,
  initialFeature,
  landFrom,
  onClose,
  ...props
}: Props) => {
  const {
    t
  } = useTranslation();
  const [selectedPlanId, setSelectedPlanId] = useState<string>();
  const closeModal = useSetAtom(closeModalAtom);
  const {
    data: subscriptionData
  } = useSubscriptionPageQuery();
  const subscriptionEventData = useSubscriptionEventData();
  const hasImpressionFiredRef = useRef(false);
  useEffect(() => {
    // 딥링크, 성별, 샵일때 default page id를 설정해줌
    if (!selectedPlanId || hasImpressionFiredRef.current) return;
    hasImpressionFiredRef.current = true;
    pushEvent({
      eventType: EVENT_TYPE.SUBSCRIPTION,
      eventName: EVENT_NAME.SUBS__IMPRESSION,
      eventParams: {
        current_page_id: selectedPlanId,
        initial_feature: initialFeature ?? 'none',
        initial_page_id: defaultPageId ?? selectedPlanId,
        land_from: landFrom,
        ...subscriptionEventData.eventParams,
        // 웹에서는 스토어 계정이 없기때문에 is_store~ 관련 로그는 모두 false 처리
        is_store_account_active: false,
        is_store_account_matched: false
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlanId]);
  useEffect(() => {
    if (!subscriptionData?.pages.length || selectedPlanId) return;
    const {
      pages
    } = subscriptionData;
    const defaultPage = defaultPageId ? pages.find(p => p.pageId === defaultPageId) : undefined;
    const corePage = pages.find(p => p.pageId === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_CORE);
    setSelectedPlanId((defaultPage ?? corePage ?? pages[0]).pageId);
  }, [subscriptionData, selectedPlanId, defaultPageId]);
  const pages = subscriptionData?.pages ?? [];
  const {
    planColumns,
    benefitRows
  } = useMemo(() => subscriptionData ? buildBenefitTableData(subscriptionData) : {
    planColumns: [],
    benefitRows: []
  }, [subscriptionData]);
  const page = pages.find(p => p.pageId === selectedPlanId);
  const isHighPlan = planColumns.find(col => col.id === selectedPlanId)?.isHighPlan ?? false;
  const showWebDiscountLabel = !!page?.webDiscountText;
  const showFirstTimeDiscountLabel = !!page?.pgIntroductoryOfferEligible && !!page?.firstSubscriptionDescription;
  const showLabelContainer = showWebDiscountLabel || showFirstTimeDiscountLabel;
  const {
    buttonProps,
    handleButtonClick
  } = useSubscriptionGuideButton(selectedPlanId, pages, {
    initialFeature,
    landFrom,
    defaultPlanId: defaultPageId
  });
  const handlePlanSelect = (planId: string) => {
    if (planId === selectedPlanId) return;
    pushEvent({
      eventType: EVENT_TYPE.SUBSCRIPTION,
      eventName: EVENT_NAME.SUBS__CHANGE_TAB,
      eventParams: {
        from: selectedPlanId,
        to: planId,
        ...subscriptionEventData.eventParams,
        is_store_account_active: false,
        is_store_account_matched: false
      }
    });
    setSelectedPlanId(planId);
  };
  const handleClose = () => {
    closeModal(ModalType.SUBSCRIPTION_GUIDE);
    onClose?.();
  };
  return <ModalLayout {...props} mobileType={MobileModalType.SCREEN_MODAL} mobileNavButtonType={ModalNavButtonType.NONE} navButtonType={ModalNavButtonType.EXTERNAL_CLOSE} onClose={handleClose} closeWhenClickOutside={false} isBlur data-sentry-element="ModalLayout" data-sentry-component="SubscriptionGuideModal" data-sentry-source-file="index.tsx">
      <MobileCloseButton onClick={handleClose} data-sentry-element="MobileCloseButton" data-sentry-source-file="index.tsx">
        <IcCloseBold data-sentry-element="IcCloseBold" data-sentry-source-file="index.tsx" />
      </MobileCloseButton>
      <Container data-sentry-element="Container" data-sentry-source-file="index.tsx">
        <LeftPanel $isHighPlan={isHighPlan} data-sentry-element="LeftPanel" data-sentry-source-file="index.tsx">
          <BlurTopWrapper data-sentry-element="BlurTopWrapper" data-sentry-source-file="index.tsx">
            <BlurImage src='/images/subscription/blurCore.png' alt='' $isVisible={!isHighPlan} data-sentry-element="BlurImage" data-sentry-source-file="index.tsx" />
            <BlurImage src='/images/subscription/blurMax.png' alt='' $isVisible={isHighPlan} data-sentry-element="BlurImage" data-sentry-source-file="index.tsx" />
          </BlurTopWrapper>
          <TitleSection data-sentry-element="TitleSection" data-sentry-source-file="index.tsx">
            {showLabelContainer && <LabelContainer>
                {showWebDiscountLabel && <WebOnlyDiscountLabel iconUrl={page.webDiscountIconUrl}>
                    {page.webDiscountText}
                  </WebOnlyDiscountLabel>}
                {showFirstTimeDiscountLabel && <FirstTimeDiscountLabel>
                    {page.firstSubscriptionDescription}
                  </FirstTimeDiscountLabel>}
              </LabelContainer>}
            {page && <Title>{page.subscriptionDescription}</Title>}
          </TitleSection>
        </LeftPanel>

        <RightPanel data-sentry-element="RightPanel" data-sentry-source-file="index.tsx">
          <TabsWrapper data-sentry-element="TabsWrapper" data-sentry-source-file="index.tsx">
            <SubscriptionTabs planColumns={planColumns} selectedPlanId={selectedPlanId} onPlanSelect={handlePlanSelect} data-sentry-element="SubscriptionTabs" data-sentry-source-file="index.tsx" />
          </TabsWrapper>
          <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
            <ListWrapper data-sentry-element="ListWrapper" data-sentry-source-file="index.tsx">
              <SubscriptionFeatureList defaultPlanId={defaultPageId} selectedPlanId={selectedPlanId} initialFeature={initialFeature} landFrom={landFrom} planColumns={planColumns} benefitRows={benefitRows} onPlanSelect={handlePlanSelect} data-sentry-element="SubscriptionFeatureList" data-sentry-source-file="index.tsx" />
            </ListWrapper>
          </ModalContent>
          <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
            {buttonProps && <SubscriptionPurchaseButton isHighTier={buttonProps.planId === INVENTORY_ITEM_CATEGORY_ID_TYPES.AZAR_MAX} priceInfo={buttonProps.priceInfo} onClick={handleButtonClick} disabled={buttonProps.disabled} loading={buttonProps.loading}>
                {t(buttonProps.textKey)}
              </SubscriptionPurchaseButton>}
          </ModalFooter>
        </RightPanel>
      </Container>
    </ModalLayout>;
};
export default SubscriptionGuideModal;